/**
 * @fileoverview Memoization Utility - Provides memoization functionality for expensive computations
 * @description This utility implements memoization with LRU cache for function results
 * @version 1.0.0
 * @author Dashboard Team
 * @created 2024-01-01
 * @updated 2024-01-01
 */

interface MemoizedFunction<T extends (...args: any[]) => any> {
  (...args: Parameters<T>): ReturnType<T>;
  cache: Map<string, ReturnType<T>>;
  clear: () => void;
  has: (key: string) => boolean;
  delete: (key: string) => boolean;
  size: () => number;
}

interface MemoizeOptions {
  maxSize?: number;
  ttl?: number; // Time to live in milliseconds
  keyGenerator?: (...args: any[]) => string;
}

/**
 * @function memoize
 * @description Memoizes a function with LRU cache
 * @param fn - Function to memoize
 * @param options - Memoization options
 * @returns Memoized function
 */
export function memoize<T extends (...args: any[]) => any>(
  fn: T,
  options: MemoizeOptions = {}
): MemoizedFunction<T> {
  const {
    maxSize = 100,
    ttl = 5 * 60 * 1000, // 5 minutes
    keyGenerator = (...args: any[]) => JSON.stringify(args)
  } = options;

  const cache = new Map<string, { value: ReturnType<T>; timestamp: number }>();
  const accessOrder = new Map<string, number>();
  let accessCounter = 0;

  const memoized = (...args: Parameters<T>): ReturnType<T> => {
    const key = keyGenerator(...args);
    const now = Date.now();

    // Check if result is in cache and not expired
    const cached = cache.get(key);
    if (cached) {
      if (now - cached.timestamp < ttl) {
        // Update access order
        accessOrder.set(key, ++accessCounter);
        return cached.value;
      } else {
        // Remove expired entry
        cache.delete(key);
        accessOrder.delete(key);
      }
    }

    // Execute function and cache result
    const result = fn(...args);
    
    // Remove oldest entries if cache is full
    if (cache.size >= maxSize) {
      evictOldest();
    }

    cache.set(key, { value: result, timestamp: now });
    accessOrder.set(key, ++accessCounter);

    return result;
  };

  function evictOldest(): void {
    let oldestKey = '';
    let oldestAccess = Infinity;

    for (const [key, access] of accessOrder.entries()) {
      if (access < oldestAccess) {
        oldestAccess = access;
        oldestKey = key;
      }
    }

    if (oldestKey) {
      cache.delete(oldestKey);
      accessOrder.delete(oldestKey);
    }
  }

  // Add cache management methods
  (memoized as MemoizedFunction<T>).cache = new Map();
  (memoized as MemoizedFunction<T>).clear = () => {
    cache.clear();
    accessOrder.clear();
    accessCounter = 0;
  };
  (memoized as MemoizedFunction<T>).has = (key: string) => {
    const cached = cache.get(key);
    if (!cached) return false;
    
    const now = Date.now();
    if (now - cached.timestamp >= ttl) {
      cache.delete(key);
      accessOrder.delete(key);
      return false;
    }
    
    return true;
  };
  (memoized as MemoizedFunction<T>).delete = (key: string) => {
    const deleted = cache.delete(key);
    accessOrder.delete(key);
    return deleted;
  };
  (memoized as MemoizedFunction<T>).size = () => cache.size;

  return memoized as MemoizedFunction<T>;
}

/**
 * @function debounce
 * @description Debounces a function call
 * @param fn - Function to debounce
 * @param delay - Delay in milliseconds
 * @returns Debounced function
 */
export function debounce<T extends (...args: any[]) => any>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;

  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

/**
 * @function throttle
 * @description Throttles a function call
 * @param fn - Function to throttle
 * @param delay - Delay in milliseconds
 * @returns Throttled function
 */
export function throttle<T extends (...args: any[]) => any>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let lastCall = 0;

  return (...args: Parameters<T>) => {
    const now = Date.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      fn(...args);
    }
  };
}

/**
 * @function once
 * @description Ensures a function is called only once
 * @param fn - Function to call once
 * @returns Function that calls the original function only once
 */
export function once<T extends (...args: any[]) => any>(
  fn: T
): (...args: Parameters<T>) => ReturnType<T> | undefined {
  let called = false;
  let result: ReturnType<T>;

  return (...args: Parameters<T>) => {
    if (!called) {
      called = true;
      result = fn(...args);
    }
    return result;
  };
}

/**
 * @function memoizeAsync
 * @description Memoizes an async function
 * @param fn - Async function to memoize
 * @param options - Memoization options
 * @returns Memoized async function
 */
export function memoizeAsync<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  options: MemoizeOptions = {}
): MemoizedFunction<T> {
  const {
    maxSize = 100,
    ttl = 5 * 60 * 1000,
    keyGenerator = (...args: any[]) => JSON.stringify(args)
  } = options;

  const cache = new Map<string, { value: ReturnType<T>; timestamp: number }>();
  const pending = new Map<string, Promise<ReturnType<T>>>();
  const accessOrder = new Map<string, number>();
  let accessCounter = 0;

  const memoized = async (...args: Parameters<T>): Promise<ReturnType<T>> => {
    const key = keyGenerator(...args);
    const now = Date.now();

    // Check if result is in cache and not expired
    const cached = cache.get(key);
    if (cached) {
      if (now - cached.timestamp < ttl) {
        accessOrder.set(key, ++accessCounter);
        return cached.value;
      } else {
        cache.delete(key);
        accessOrder.delete(key);
      }
    }

    // Check if request is already pending
    if (pending.has(key)) {
      return pending.get(key)!;
    }

    // Execute function and cache result
    const promise = fn(...args);
    pending.set(key, promise);

    try {
      const result = await promise;
      
      // Remove oldest entries if cache is full
      if (cache.size >= maxSize) {
        evictOldest();
      }

      cache.set(key, { value: result, timestamp: now });
      accessOrder.set(key, ++accessCounter);
      
      return result;
    } finally {
      pending.delete(key);
    }
  };

  function evictOldest(): void {
    let oldestKey = '';
    let oldestAccess = Infinity;

    for (const [key, access] of accessOrder.entries()) {
      if (access < oldestAccess) {
        oldestAccess = access;
        oldestKey = key;
      }
    }

    if (oldestKey) {
      cache.delete(oldestKey);
      accessOrder.delete(oldestKey);
    }
  }

  // Add cache management methods
  (memoized as MemoizedFunction<T>).cache = new Map();
  (memoized as MemoizedFunction<T>).clear = () => {
    cache.clear();
    accessOrder.clear();
    pending.clear();
    accessCounter = 0;
  };
  (memoized as MemoizedFunction<T>).has = (key: string) => {
    const cached = cache.get(key);
    if (!cached) return false;
    
    const now = Date.now();
    if (now - cached.timestamp >= ttl) {
      cache.delete(key);
      accessOrder.delete(key);
      return false;
    }
    
    return true;
  };
  (memoized as MemoizedFunction<T>).delete = (key: string) => {
    const deleted = cache.delete(key);
    accessOrder.delete(key);
    pending.delete(key);
    return deleted;
  };
  (memoized as MemoizedFunction<T>).size = () => cache.size;

  return memoized as MemoizedFunction<T>;
}

// Common memoization patterns
export const memoizeDateCalculation = memoize(
  (year: number, month: number) => new Date(year, month, 1),
  { maxSize: 50, ttl: 60 * 1000 } // 1 minute
);

export const memoizeStringFormat = memoize(
  (template: string, ...args: any[]) => {
    return template.replace(/{(\d+)}/g, (match, index) => {
      return args[index] || match;
    });
  },
  { maxSize: 200, ttl: 5 * 60 * 1000 }
);

export const memoizeApiKey = memoize(
  (endpoint: string, params: Record<string, any>) => {
    const sortedKeys = Object.keys(params).sort();
    const paramString = sortedKeys.map(key => `${key}=${params[key]}`).join('&');
    return `${endpoint}?${paramString}`;
  },
  { maxSize: 100, ttl: 10 * 60 * 1000 }
);
